import { useCallback, useEffect, useRef, useState } from "react";

import { fetchNav2LogsTail } from "lib/nav2LogsApi";

export const NAV_SERVICE_LOG_MAX_LINES = 2000;
const TAIL_LINES = 200;

type UseNavServiceLogStreamOptions = {
    baseUrl: string | null;
    streamUrl: string | null;
    enabled?: boolean;
};

export function useNavServiceLogStream({
    baseUrl,
    streamUrl,
    enabled = true,
}: UseNavServiceLogStreamOptions) {
    const [lines, setLines] = useState<string[]>([]);
    const [connected, setConnected] = useState(false);
    const [paused, setPaused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pausedRef = useRef(paused);

    pausedRef.current = paused;

    const appendLine = useCallback((line: string) => {
        if (pausedRef.current) return;
        setLines((prev) => {
            const next = [...prev, line];
            return next.length > NAV_SERVICE_LOG_MAX_LINES
                ? next.slice(-NAV_SERVICE_LOG_MAX_LINES)
                : next;
        });
    }, []);

    const clear = useCallback(() => {
        setLines([]);
    }, []);

    useEffect(() => {
        if (!enabled || !baseUrl || paused) {
            setConnected(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        void (async () => {
            try {
                const tail = await fetchNav2LogsTail(baseUrl, TAIL_LINES);
                if (cancelled) return;
                setLines(tail.lines.slice(-NAV_SERVICE_LOG_MAX_LINES));
            } catch (e) {
                if (cancelled) return;
                const msg = e instanceof Error ? e.message : "Failed to load nav logs";
                setError(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [baseUrl, enabled, paused]);

    useEffect(() => {
        if (!enabled || !streamUrl || paused) {
            setConnected(false);
            return undefined;
        }

        const es = new EventSource(streamUrl);
        es.onopen = () => {
            setConnected(true);
            setError(null);
        };
        es.onmessage = (ev) => {
            appendLine(ev.data);
        };
        es.onerror = () => {
            setConnected(false);
            setError("Log stream disconnected");
            es.close();
        };

        return () => {
            es.close();
            setConnected(false);
        };
    }, [appendLine, enabled, paused, streamUrl]);

    const togglePaused = useCallback(() => {
        setPaused((prev) => !prev);
    }, []);

    return {
        lines,
        connected,
        paused,
        loading,
        error,
        clear,
        togglePaused,
    };
}
